import { getMaxPayableAmount } from '../functions/invoicing/max-payable-amount'

type AbpBalanceSource = {
  account: number
}

/**
 * Returns the outstanding balance (as a positive number) for the ABP flow.
 *
 * Outstanding balance is modeled as negative debt values in sale/customer balance data.
 * This helper normalizes the combined account balance into a non-negative amount that can
 * safely be used for UI caps and payment validation.
 *
 * Any missing or non-debt value resolves to `0`.
 */
export const getAbpOutstandingBalance = (
  customerBalances: AbpBalanceSource | null | undefined
): number => {
  if (!customerBalances) {
    return 0
  }

  return Math.max(-(customerBalances.account ?? 0), 0)
}

/**
 * Caps a candidate ABP amount to remaining payable
 * (|outstanding| − in-transit).
 *
 * Note: `getAbpOutstandingBalance` only returns the headline owing.
 * This helper adds input guardrails (finite/positive checks) and performs
 * the final clamp in one place for ABP flows.
 *
 * Invalid, non-finite, zero, or negative amounts return `0`.
 * Valid positive amounts are clamped to remaining payable.
 */
export const resolveAbpCappedAmount = (
  amount: number,
  customerBalances: AbpBalanceSource | null | undefined,
  inTransitAmount = 0
): number => {
  if (!Number.isFinite(amount) || amount <= 0) {
    return 0
  }

  return Math.min(
    amount,
    getMaxPayableAmount(
      getAbpOutstandingBalance(customerBalances),
      inTransitAmount
    )
  )
}
